import type {
    CatalogRecord,
    ComplianceStatus,
    EnrichedPart,
    LifecycleStatus,
    ManufacturerProfile,
    PartCategory,
    SupplierSource,
} from "../domain/types";
import { taxonomyForCategory, type DemoGeneratorConfig } from "./generatorConfig";
import { roundTo, SeededRandom } from "./seededRandom";

const PACKAGES = ["0402", "0603", "0805", "QFN-32", "SOIC-8", "SOT-23", "BGA-144"];
const SOURCES: SupplierSource[] = ["manufacturer_direct", "enrichment_provider", "supplier_submitted"];
const COMPLIANCE: ComplianceStatus[] = ["Compliant", "Non-compliant", "Unknown"];

const chooseCategory = (rng: SeededRandom, manufacturer: ManufacturerProfile, index: number): PartCategory => {
    if (index % 3 !== 0) return manufacturer.preferredCategory;
    return rng.pick(["Semiconductor", "Capacitor", "Resistor", "Connector"]);
};

const lifecycleForIndex = (index: number, rng: SeededRandom): LifecycleStatus => {
    if (index % 41 === 0) return "EOL";
    if (index % 29 === 0) return "NRND";
    if (index % 17 === 0) return "Preliminary";
    return rng.bool(0.92) ? "Active" : "Preliminary";
};

const buildMpn = (manufacturer: ManufacturerProfile, category: PartCategory, index: number, hasMpnFormatIssue: boolean): string => {
    if (hasMpnFormatIssue) {
        if (index % 2 === 0) return "TBD";
        return ` ${manufacturer.id}-${index} `;
    }

    const suffix = `${String(index).padStart(5, "0")}${category.slice(0, 3).toUpperCase()}`;
    if (manufacturer.name === "Texas Instruments") return `TI-${suffix}`;
    if (manufacturer.name === "Murata") return `GRM${suffix}`;
    if (manufacturer.name === "Yageo") return `RC${suffix}`;
    if (manufacturer.name === "Vishay") return `VJ${suffix}`;
    if (manufacturer.name === "STMicroelectronics") return `STM${suffix}`;
    return `NEW-${suffix}`;
};

const getProductGroup = (category: PartCategory): string => {
    if (category === "Semiconductor") return "SEM";
    if (category === "Capacitor") return "PAS-CAP";
    if (category === "Resistor") return "PAS-RES";
    return "INT-CON";
};

export const generateParts = (
    config: Pick<DemoGeneratorConfig, "partCount" | "catalogCount">,
    rng: SeededRandom,
    manufacturers: ManufacturerProfile[],
): { parts: EnrichedPart[]; catalog: CatalogRecord[] } => {
    const parts: EnrichedPart[] = [];
    const catalog: CatalogRecord[] = [];

    for (let index = 0; index < config.partCount; index += 1) {
        const manufacturer = manufacturers[index % manufacturers.length]!;
        const category = chooseCategory(rng, manufacturer, index);
        const taxonomy = taxonomyForCategory(category, index);
        const taxonomyPath = index % 83 === 0 ? "Passive > Inductor > Power" : taxonomy.taxonomyPath;
        const hasMandatoryAttributeGap = category === "Semiconductor" && index % 23 === 0;
        const hasUomConflict = (category === "Capacitor" || category === "Resistor") && index % 31 === 0;
        const hasMpnFormatIssue = index % 97 === 0;
        const hasComplianceChange = index % 67 === 0;
        const hasSourceConflict = index % 53 === 0;
        const lifecycleStatus = lifecycleForIndex(index, rng);
        const priorLifecycleStatus: LifecycleStatus = lifecycleStatus === "Active" ? "Active" : rng.bool(0.65) ? "Active" : lifecycleStatus;
        const rohsStatus = hasMandatoryAttributeGap ? null : hasComplianceChange ? "Non-compliant" : rng.pick(COMPLIANCE);
        const partId = `PART-${String(index + 1).padStart(6, "0")}`;
        const mpn = buildMpn(manufacturer, category, index, hasMpnFormatIssue);
        const confidenceScore = index % 37 === 0 ? rng.integer(45, 79) : rng.integer(80, 99);

        const part: EnrichedPart = {
            partId,
            mpn,
            manufacturerId: manufacturer.id,
            manufacturerName: manufacturer.name,
            category,
            taxonomyPath,
            packageType: hasMandatoryAttributeGap ? null : rng.pick(PACKAGES),
            pinCount: category === "Semiconductor" ? (hasMandatoryAttributeGap ? null : rng.pick([8, 16, 24, 32, 64, 100, 144])) : null,
            voltageMin: category === "Semiconductor" && hasMandatoryAttributeGap ? null : roundTo(rng.float(1.2, 12, 2), 2),
            voltageMax: category === "Semiconductor" && hasMandatoryAttributeGap ? null : roundTo(rng.float(12, 60, 2), 2),
            capacitancePf: category === "Capacitor" ? rng.pick([10, 100, 1000, 10000, 100000, 1000000]) : null,
            resistanceOhms: category === "Resistor" ? rng.pick([10, 47, 100, 1000, 10000, 100000]) : null,
            tolerancePct: category === "Connector" ? null : rng.pick([1, 2, 5, 10, 20]),
            rohsStatus,
            reachStatus: rng.pick(COMPLIANCE),
            lifecycleStatus,
            priorLifecycleStatus,
            confidenceScore,
            source: rng.pick(SOURCES),
            recommendedReplacementMpn: lifecycleStatus === "EOL" || lifecycleStatus === "NRND" ? `${mpn}-ALT` : null,
            hasMandatoryAttributeGap,
            hasUomConflict,
            hasMpnFormatIssue,
            hasComplianceChange,
            hasSourceConflict,
        };

        parts.push(part);

        if (catalog.length < config.catalogCount && index % 4 === 0) {
            catalog.push({
                catalogId: `CAT-${String(catalog.length + 1).padStart(6, "0")}`,
                partId,
                mpn,
                manufacturerId: manufacturer.id,
                lifecycleStatus: priorLifecycleStatus,
                rohsStatus: hasComplianceChange ? "Compliant" : rohsStatus,
                productGroup: getProductGroup(category),
                productLine: taxonomy.erpProductLine,
                published: lifecycleStatus === "Active",
                erpItemId: `ERP-${String(catalog.length + 1).padStart(5, "0")}`,
                crmProductId: `CRM-${String(catalog.length + 1).padStart(5, "0")}`,
            });
        }
    }

    return { parts, catalog };
};
